<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'exportSuccess', 'exportError']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isExporting = ref(false);
const exportError = ref(null);

const modalTitle = computed(() =>
  t('CAMPAIGN.EXPORT.MODAL.TITLE', {
    campaignName: props.campaign?.title || 'Campaign',
  })
);

// Watch for show prop changes to open/close dialog
watch(
  () => props.show,
  newShow => {
    if (newShow) {
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

const handleExport = async () => {
  if (!props.campaign?.id) {
    exportError.value = 'Invalid campaign data';
    return;
  }

  isExporting.value = true;
  exportError.value = null;

  try {
    await store.dispatch('campaigns/exportReport', {
      campaignId: props.campaign.id,
    });

    emit('exportSuccess');
    emit('close');
  } catch (error) {
    exportError.value = error.message || 'Export failed. Please try again.';
    emit('exportError', error);
  } finally {
    isExporting.value = false;
  }
};

const handleClose = () => {
  if (!isExporting.value) {
    emit('close');
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="modalTitle"
    width="md"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <!-- Campaign Info -->
      <div class="bg-n-alpha-2 rounded-lg p-4">
        <h3 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.EXPORT.MODAL.CAMPAIGN_INFO') }}
        </h3>
        <div class="space-y-1 text-sm text-n-slate-11">
          <div>
            <strong>{{ t('CAMPAIGN.EXPORT.MODAL.NAME') }}:</strong>
            {{ campaign?.title }}
          </div>
          <div>
            <strong>{{ t('CAMPAIGN.EXPORT.MODAL.STATUS') }}:</strong>
            {{ campaign?.campaign_status }}
          </div>
          <div>
            <strong>{{ t('CAMPAIGN.EXPORT.MODAL.TYPE') }}:</strong>
            {{ campaign?.inbox?.channel_type }}
          </div>
        </div>
      </div>

      <!-- Export Info -->
      <div class="bg-n-blue-1 rounded-lg p-4">
        <h3 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.EXPORT.MODAL.EXPORT_INFO') }}
        </h3>
        <div class="space-y-1 text-sm text-n-slate-11">
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <div>• {{ t('CAMPAIGN.EXPORT.MODAL.FORMAT') }}: CSV</div>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <div>
            • {{ t('CAMPAIGN.EXPORT.MODAL.CONTENT') }}: Contact details, message
            status, failure reasons
          </div>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <div>
            • {{ t('CAMPAIGN.EXPORT.MODAL.SUMMARY') }}: Total sent, failed,
            skipped counts
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="exportError"
        class="bg-n-ruby-1 border border-n-ruby-6 rounded-lg p-3"
      >
        <p class="text-sm text-n-ruby-11">{{ exportError }}</p>
      </div>
    </div>

    <!-- Action Buttons -->
    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          :disabled="isExporting"
          @click="handleClose"
        >
          {{ t('CAMPAIGN.EXPORT.MODAL.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          :loading="isExporting"
          :disabled="isExporting"
          @click="handleExport"
        >
          <template #icon>
            <Spinner v-if="isExporting" size="16" />
            <span v-else class="i-lucide-download" />
          </template>
          {{
            isExporting
              ? t('CAMPAIGN.EXPORT.MODAL.EXPORTING')
              : t('CAMPAIGN.EXPORT.MODAL.DOWNLOAD')
          }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>
